import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { message } from 'antd';
import { isTokenExpired } from './tokenUtils.ts';

// Configurazione base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Flag per evitare loop infiniti durante il refresh
let isRefreshing = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Crea istanza personalizzata
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 secondi timeout globale
});

/**
 * Funzione per effettuare il refresh del token
 */
const refreshAuthToken = async (): Promise<string> => {
  const token = localStorage.getItem('token');
  
  if (!token || isTokenExpired(token)) {
    throw new Error('No valid token available for refresh');
  }

  const response = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  const newToken = response.data.access_token;
  localStorage.setItem('token', newToken);
  return newToken;
};

// Interceptor per le richieste
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`🔑 API: Token aggiunto alla richiesta`);
    } else {
      console.log(`🔓 API: Nessun token disponibile`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor per le risposte con retry automatico
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    console.error(`❌ API Error: ${error.response?.status || 'NETWORK'} ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`, error.message);

    // Gestione errori 401 con retry automatico.
    // Esclusi: la login (401 = credenziali errate: nessuna sessione da rinnovare,
    // il messaggio deve restare nel form), il refresh stesso e le richieste senza
    // token (nulla da rinnovare: si rigetta subito senza ricaricare la pagina).
    const reqUrl = originalRequest?.url || '';
    const isAuthEndpoint = reqUrl.includes('/auth/login') || reqUrl.includes('/auth/refresh');
    const hasToken = !!localStorage.getItem('token');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && hasToken) {
      if (isRefreshing) {
        // Se è già in corso un refresh, aggiungi la richiesta alla coda
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers!.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshAttempts++;

      console.log(`🔄 Interceptor: Tentativo refresh ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}`);

      // Controllo numero massimo di tentativi
      if (refreshAttempts > MAX_REFRESH_ATTEMPTS) {
        console.log('❌ Interceptor: Raggiunti troppi tentativi di refresh, logout forzato');
        isRefreshing = false;
        refreshAttempts = 0;
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(new Error('Troppi tentativi di refresh'));
      }

      try {
        // Tenta il refresh del token con timeout
        const refreshPromise = refreshAuthToken();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout refresh token')), 8000)
        );
        
        const newToken = await Promise.race([refreshPromise, timeoutPromise]);
        
        console.log('✅ Interceptor: Refresh completato con successo');
        refreshAttempts = 0; // Reset counter su successo
        
        // Processa le richieste in coda
        failedRequestsQueue.forEach(({ resolve }) => {
          resolve(newToken);
        });
        failedRequestsQueue = [];
        
        // Riprova la richiesta originale con il nuovo token
        originalRequest.headers!.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
        
      } catch (refreshError: any) {
        console.error('❌ Interceptor: Errore durante refresh:', refreshError.message);
        
        // Il refresh è fallito, esegui logout
        failedRequestsQueue.forEach(({ reject }) => {
          reject(refreshError);
        });
        failedRequestsQueue = [];
        
        localStorage.removeItem('token');
        
        // Solo mostra messaggio se non è un timeout
        if (refreshError.message !== 'Timeout refresh token') {
          message.error('Sessione scaduta, effettuare nuovamente il login');
        }
        
        window.location.href = '/login';
        return Promise.reject(refreshError);
        
      } finally {
        isRefreshing = false;
      }
    }

    // Gestione errori di rete senza retry automatico
    if (!error.response) {
      console.error('❌ Network Error: Errore di connessione', error.message);
      message.error('Errore di connessione. Controlla la connessione di rete.');
      return Promise.reject(error);
    }

    // Gestione altri errori
    if (error.response?.status === 503) {
      // Errore di connessione database
      const errorData = error.response?.data;
      if (errorData?.error_type === 'database_connection_error') {
        message.error(errorData.detail || 'Errore di connessione al database MySQL');
      } else {
        message.error('Servizio temporaneamente non disponibile');
      }
    } else if (error.response?.status >= 500) {
      // Altri errori del server
      const errorData = error.response?.data;
      if (errorData?.error_type === 'database_error') {
        message.error(errorData.detail || 'Errore database');
      } else {
        message.error('Errore del server. Riprova più tardi.');
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
